/**
 * Asset Classification Page (/monitoring/classification)
 *
 * Displays the positions held by the institution's borrowers, grouped by the
 * asset class of each position's security (Security.assetClass).
 * Data source: GET /api/admin/collateral/distribution-by-asset-class
 *
 * The query (collateral-monitoring.routes.ts, distribution-by-asset-class)
 * groups every position whose account belongs to one of the institution's
 * borrowers. It applies no collateral-package or pledge filter, so the page
 * says "held", never "pledged" or "linked to active collateral packages".
 *
 * Renders:
 *   - Summary metric cards (total positions, total value, asset class count)
 *   - Bar chart of market value per asset class
 *   - Data table with count + value per class
 *
 * Critical path steps: 5 (System classifies assets), 10 (Aaim monitors collateral)
 */

import { useQuery } from '@tanstack/react-query';
import {
  Card,
  DataTable,
  MetricCard,
  MetricCardGrid,
  METRIC_READ_CAPTIONS,
  Skeleton,
  GroupedBarChart,
  type DataTableColumn,
} from '@aaimplatform/ui';
import { collateralAnalyticsApi, type AssetClassDistributionItem } from '../../api/services/collateral-analytics';
import {
  SECURITY_ASSET_CLASS_LABELS,
  formatPercent,
  formatUsd,
  formatUsdCompact,
  labelFor,
} from './display-format';
import { readOutcome, refreshFailed, type ReadOutcome } from './read-state';
import { ReadNotice } from './ReadNotice';

// The table carries no color swatch per asset class. The bar chart above it is
// one series drawn in one color, so a per-class swatch matched no mark on this
// page. The per-class colors live in display-format (SECURITY_ASSET_CLASS_COLORS),
// where the Health Overview donut, which does draw one mark per class, reads
// them.

const SUBJECT = 'Asset classification data';

/** The sentence a section prints when it has nothing to draw. */
function sectionMessage(outcome: ReadOutcome): string {
  if (outcome === 'failed') return `${SUBJECT} could not be read.`;
  if (outcome !== 'read') return `${SUBJECT} not yet read.`;
  return "Your institution's borrowers hold no positions, so there is nothing to classify.";
}

/** A generated distribution item plus the share this page computes from the total. */
const CLASSIFICATION_COLUMNS: DataTableColumn<AssetClassDistributionItem & { sharePct: number | null }>[] = [
  {
    header: 'Asset Class',
    accessor: 'assetClass',
    align: 'left',
    cell: (value) => (
      <span className="text-sm">{labelFor(String(value), SECURITY_ASSET_CLASS_LABELS)}</span>
    ),
  },
  {
    header: 'Positions',
    accessor: 'count',
    align: 'right',
    monospace: true,
    cell: (value) => Number(value).toLocaleString('en-US'),
  },
  {
    header: 'Total Market Value',
    accessor: 'totalValue',
    align: 'right',
    monospace: true,
    cell: (value) => formatUsd(Number(value)),
  },
  {
    header: 'Portfolio Share',
    accessor: 'sharePct',
    align: 'right',
    // A share of a zero total is not a measured 0.0%; it has no denominator.
    cell: (_value, row) => (
      <span className="font-mono text-sm text-secondary">
        {row.sharePct == null ? '--' : formatPercent(row.sharePct)}
      </span>
    ),
  },
];

function ClassificationTable({ items, totalValue }: { items: AssetClassDistributionItem[]; totalValue: number }) {
  const rows = items.map((item) => ({
    ...item,
    sharePct: totalValue > 0 ? (item.totalValue / totalValue) * 100 : null,
  }));
  return <DataTable columns={CLASSIFICATION_COLUMNS} data={rows} />;
}

export function AssetClassificationPage() {
  const query = useQuery({
    queryKey: ['admin', 'collateral', 'distribution-by-asset-class'],
    queryFn: () => collateralAnalyticsApi.getDistributionByAssetClass(),
    refetchInterval: 60000,
  });
  const outcome = readOutcome(query);

  // Figures exist only where a read succeeded. A failed or unread first read
  // prints '--' with the reason, never `?? 0`.
  const data = outcome === 'read' ? query.data : undefined;
  const distribution = data?.distribution ?? [];
  const withheldCaption =
    outcome === 'failed'
      ? METRIC_READ_CAPTIONS.failed
      : outcome === 'read'
        ? undefined
        : METRIC_READ_CAPTIONS.unread;

  const chartData = distribution.map((item) => ({
    class: labelFor(item.assetClass, SECURITY_ASSET_CLASS_LABELS),
    value: item.totalValue,
  }));

  return (
    <div>
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); no redundant H1. */}
        <p className="max-w-content-narrow text-sm font-sans text-tertiary">
          Positions held by your institution&apos;s borrowers, grouped by asset
          class. Positions are counted whether or not they are pledged to a
          collateral package.
        </p>
      </div>

      <ReadNotice
        subject={SUBJECT}
        failed={outcome === 'failed'}
        refreshFailed={refreshFailed(query)}
        dataUpdatedAt={query.dataUpdatedAt}
        error={query.error}
        onRetry={() => {
          void query.refetch();
        }}
      />

      {/* Summary metric cards: the kit tile row, two across on a phone (UI run
          r3, fi-cons-13). */}
      <MetricCardGrid columns={3} className="mb-8">
        {outcome === 'loading' ? (
          <>
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </>
        ) : (
          <>
            <MetricCard
              label="Total Positions"
              value={data ? data.totalPositions.toLocaleString('en-US') : '--'}
              caption={withheldCaption}
              description={data ? "Positions held by your institution's borrowers" : undefined}
            />
            <MetricCard
              label="Total Market Value"
              value={data ? formatUsdCompact(data.totalValue) : '--'}
              caption={withheldCaption}
              description={data ? 'Aggregate market value of those positions' : undefined}
            />
            <MetricCard
              label="Asset Classes"
              value={data ? String(distribution.length) : '--'}
              caption={withheldCaption}
              description={data ? 'Distinct asset classes represented' : undefined}
            />
          </>
        )}
      </MetricCardGrid>

      {/* Bar chart. One series: every bar is the same quantity (market value),
          so the legend names the quantity rather than the first asset class. */}
      <div className="mb-8">
        {outcome === 'loading' ? (
          <Card>
            <Skeleton className="h-64" />
          </Card>
        ) : chartData.length > 0 ? (
          <GroupedBarChart
            title="Market Value by Asset Class"
            data={chartData}
            dataKeys={[{ key: 'value', name: 'Market value', color: 'rgb(var(--color-growth))' }]}
            xAxisKey="class"
            formatValue={formatUsdCompact}
            height={280}
          />
        ) : (
          <Card>
            <div className="flex items-center justify-center h-40 text-secondary font-sans text-sm text-center px-4">
              {sectionMessage(outcome)}
            </div>
          </Card>
        )}
      </div>

      {/* Detail table */}
      <div>
        <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-3">
          Breakdown by Asset Class
        </div>
        {outcome === 'loading' ? (
          <Skeleton className="h-48" />
        ) : data && distribution.length > 0 ? (
          <ClassificationTable items={distribution} totalValue={data.totalValue} />
        ) : (
          <Card>
            <div className="flex items-center justify-center h-20 text-secondary font-sans text-sm text-center px-4">
              {sectionMessage(outcome)}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
